/**
 * The Report panel on a wireline job (fieldiq-api#1339, WL-3): the job's
 * end-of-job service report, or the way to make one.
 *
 * One LIVE report per job (draft or issued). When there is one, the panel
 * opens it; voided reports are listed under it. With none, Create report makes
 * a draft (WL_DISPATCH) and opens it. The API refuses a draft or cancelled job
 * (409 job_not_scheduled / job_cancelled) and a second live report
 * (409 report_exists, which carries the existing one's id — opened instead).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useCreateWlReport, useWlReports } from '../../hooks/useWlReports';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { wlExistingReportId, wlReportNumber, wlReportRefusal, wlReportStatusSkin } from '../../lib/wireline-slickline-reports';
import type { WlJobDetail } from '../../types/wireline-slickline-jobs';
import type { WlReport } from '../../types/wireline-slickline-reports';
import { WL_PRIMARY, WL_SECONDARY, WlBadge, WlEmpty, WlFormError, WlLoading, WlPanel } from './WlBits';

export function JobReportPanel({ job, canWrite, timeZone }: { job: WlJobDetail; canWrite: boolean; timeZone: string | null }) {
  const navigate = useNavigate();
  const query = useWlReports({ jobId: job.id });
  const create = useCreateWlReport();
  const [error, setError] = useState<string | null>(null);
  const reports = query.data?.data ?? [];
  const live = reports.find((r) => r.status === 'draft' || r.status === 'issued') ?? null;
  const voided = reports.filter((r) => r.status === 'void');
  const failure = queryLoadFailure(query);
  const open = (r: Pick<WlReport, 'id'>) => navigate(`/wireline-slickline/reports/${r.id}`);

  const make = () => {
    setError(null);
    create.mutate(
      { jobId: job.id },
      {
        onSuccess: (res) => {
          if (res.data?.id) navigate(`/wireline-slickline/reports/${res.data.id}`);
        },
        onError: (err) => {
          const existing = wlExistingReportId(err);
          if (existing) navigate(`/wireline-slickline/reports/${existing}`);
          else setError(wlReportRefusal(err, 'The report could not be created.'));
        },
      },
    );
  };

  return (
    <WlPanel
      eyebrow="End of job"
      title="Service report"
      testId="wl-job-report"
      actions={
        !query.isLoading && !failure && !live && canWrite ? (
          <button type="button" className={WL_PRIMARY} disabled={create.isPending} onClick={make} data-testid="wl-job-report-create">
            <Plus className="size-4" aria-hidden="true" />
            {create.isPending ? 'Creating…' : 'Create report'}
          </button>
        ) : undefined
      }
    >
      <div className="space-y-3 px-4 py-4">
        <WlFormError message={error} />
        {query.isLoading ? (
          <WlLoading label="Loading report" />
        ) : failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this job's report" />
        ) : live ? (
          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="wl-job-report-live">
            <div className="flex flex-wrap items-center gap-2">
              <FileText className="size-4 text-fieldiq-muted" aria-hidden="true" />
              <span className="font-mono text-sm font-extrabold text-white">{wlReportNumber(live)}</span>
              <WlBadge skin={wlReportStatusSkin(live.status)} value={live.status} />
              <span className="text-xs text-fieldiq-muted">
                {live.status === 'issued' ? `Issued ${formatInstant(live.issuedAt, timeZone) ?? '—'}` : 'Not issued yet'}
              </span>
            </div>
            <button type="button" className={WL_SECONDARY} onClick={() => open(live)} data-testid="wl-job-report-open">
              Open report
            </button>
          </div>
        ) : (
          <WlEmpty>
            {job.status === 'draft'
              ? 'This job is a draft. A report can be made once it is scheduled.'
              : job.status === 'cancelled'
                ? 'This job is cancelled, so it has no report.'
                : 'This job has no service report yet.'}
          </WlEmpty>
        )}
        {voided.length > 0 && (
          <ul className="space-y-1 border-t border-fieldiq-border pt-3 text-sm text-fieldiq-text" data-testid="wl-job-report-voided">
            {voided.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2">
                <button type="button" className="font-mono text-fi-amber underline-offset-2 hover:underline" onClick={() => open(r)}>
                  {wlReportNumber(r)}
                </button>
                <WlBadge skin={wlReportStatusSkin(r.status)} value={r.status} />
                <span className="text-xs text-fieldiq-muted">{r.voidReason || '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </WlPanel>
  );
}
